import { useMediaStorageConfig } from '../../features/settings';
import { formatDateTime } from '../../lib/format';

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';

function StatusRow({
  label,
  ready,
  readyText = '已配置',
  pendingText = '未配置',
}: {
  label: string;
  ready: boolean;
  readyText?: string;
  pendingText?: string;
}) {
  return (
    <div className="flex items-center justify-between text-sm text-gray-700">
      <span>{label}</span>
      <span className={ready ? 'text-emerald-600' : 'text-amber-600'}>
        {ready ? readyText : pendingText}
      </span>
    </div>
  );
}

export function MediaStoragePage() {
  const { data, form, loading, saving, error, ready, warnings, updateField, save, reload } =
    useMediaStorageConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="space-y-2">
        <p className="text-sm text-gray-500">
          管理后台和 App 自有图片上传所使用的 COS 存储桶参数。数据库配置优先生效，未填写项继续使用服务端环境变量兜底。
        </p>
        {warnings.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-700 space-y-1">
            {warnings.map((warning) => (
              <div key={warning}>• {warning}</div>
            ))}
          </div>
        )}
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr,0.8fr]">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm text-gray-700">Bucket</label>
              <input
                type="text"
                value={form.bucket}
                onChange={(e) => updateField('bucket', e.target.value)}
                className={inputCls}
                placeholder="留空使用环境变量"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">Region</label>
              <input
                type="text"
                value={form.region}
                onChange={(e) => updateField('region', e.target.value)}
                className={inputCls}
                placeholder="例如 ap-guangzhou"
                autoComplete="off"
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm text-gray-700">公网访问域名</label>
              <input
                type="text"
                value={form.public_domain}
                onChange={(e) => updateField('public_domain', e.target.value)}
                className={inputCls}
                placeholder="https://..."
                autoComplete="off"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">Secret ID</label>
              <input
                type="text"
                value={form.secret_id}
                onChange={(e) => updateField('secret_id', e.target.value)}
                className={inputCls}
                placeholder="留空使用环境变量"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">Secret Key</label>
              <input
                type="password"
                value={form.secret_key}
                onChange={(e) => updateField('secret_key', e.target.value)}
                className={inputCls}
                placeholder={data?.secret_key_configured ? '已配置，留空表示不修改' : '请输入 Secret Key'}
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900">生效状态</h3>
              <p className="mt-1 text-xs text-gray-500">
                上传签名接口会实时读取这里的最终生效配置。保存后无需重启服务。
              </p>
            </div>
            <div className="space-y-3">
              <StatusRow label="整体就绪" ready={ready || Boolean(data?.ready)} readyText="可签发" pendingText="不可签发" />
              <StatusRow label="Bucket" ready={Boolean(form.bucket.trim() || data?.bucket_configured)} />
              <StatusRow label="Region" ready={Boolean(form.region.trim() || data?.region_configured)} />
              <StatusRow label="公网域名" ready={Boolean(form.public_domain.trim() || data?.public_domain_configured)} />
              <StatusRow label="Secret ID" ready={Boolean(form.secret_id.trim() || data?.secret_id_configured)} />
              <StatusRow label="Secret Key" ready={Boolean(form.secret_key.trim() || data?.secret_key_configured)} />
              <StatusRow label="环境变量兜底" ready={Boolean(data?.using_env_fallback)} readyText="使用中" pendingText="未使用" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
          >
            {saving && (
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            保存配置
          </button>
        </div>
      </section>

      {data?.updated_at && (
        <div className="text-xs text-gray-400 space-y-1">
          <p>最后更新：{formatDateTime(data.updated_at)}</p>
          {data.updated_by != null && <p>操作人 ID：{data.updated_by}</p>}
        </div>
      )}
    </div>
  );
}
